import React, { useCallback, useRef } from 'react';
import { Node, Edge, NodeMouseHandler, NodeDragHandler, useReactFlow, XYPosition, Rect } from 'reactflow'; // Added useReactFlow, XYPosition, Rect
import useStore from '../../store/useStore';
import { useNotifications } from '../../contexts/NotificationContext';
import { useProximityConnections } from './useProximityConnections';
import { debounce } from './utils';
import { NodeCategory, NodeHoverState } from './config';
import { CustomNode, AnyNodeData } from '../../types'; // Import CustomNode and AnyNodeData
import { getHelperLines, HelperLineSegment } from '../../utils/helperLines';
import { getDefaultNodeWidth, NODE_DIMENSIONS } from '../../utils/nodeHelpers';

import { PlaybookNodeData } from '../../store/usePlaybookStore';

// Define the return type for the hook
interface NodeDragHandlers {
    onNodeDragStart: NodeMouseHandler;
    onNodeDrag: NodeDragHandler;
    onNodeDragStop: NodeDragHandler;
}

// Define a union type for the addNodeFunction
type AddNodeFunction = (node: CustomNode | Node<PlaybookNodeData>) => void;
// Define a type for the batch add nodes function
type AddNodesFunction = (nodes: Node[] | Node<PlaybookNodeData>[]) => void;


export const useNodeDragHandlers = (
    isEmbedded: boolean,
    isShiftPressed: boolean,
    isAltPressed: boolean,
    takeSnapshot: () => void,
    addNodeFunction: AddNodeFunction, // Pass the correct add node function
    addNodesFunction: AddNodesFunction, // Pass the batch add nodes function
    mode: 'project' | 'playbook' // Pass the current mode
): NodeDragHandlers => {
    const { getClosestEdge } = useProximityConnections(isShiftPressed);
    const { showNotification } = useNotifications();
    const { getNodes, getNode, project, setNodes: setReactFlowNodes } = useReactFlow(); // Get setNodes as setReactFlowNodes
    const setNodes = useStore((state) => state.setNodes); // Zustand setNodes
    // addNode is now passed as addNodeFunction
    const setEdges = useStore((state) => state.setEdges);
    const setIsNodeDragging = useStore((state) => state.setIsNodeDragging);
    const setHoveredGroupId = useStore((state) => state.setHoveredGroupId); // Get setter for hovered group
    const setHelperLines = useStore((state) => state.setHelperLines);

    const overlappingNodeRef = useRef<Node | null>(null); // Keep for electrical component logic
    const wasClickRef = useRef<boolean>(false); // Ref to track if the last interaction was a click
    const previewNodeIdRef = useRef<string | null>(null); // For copy preview node ID
    const lastPreviewPositionRef = useRef<XYPosition | null>(null); // For copy preview position
    const draggedNodeInitialStateRef = useRef<{ id: string; position: XYPosition } | null>(null); // Store initial drag state

    const onNodeDragStart: NodeMouseHandler = useCallback((_event, node) => {
        if (isEmbedded) return;
        wasClickRef.current = false;
        draggedNodeInitialStateRef.current = { id: node.id, position: { ...node.position } }; // Store initial state
        
        // Snapshot is taken for both move and potential copy (to preserve original state if copied)
        // For a move, this is the start of the move.
        // For a copy, this captures the state before the new node is added.
        takeSnapshot(); 
        
        setIsNodeDragging(true);
    }, [isEmbedded, setIsNodeDragging, takeSnapshot]);

    const lastDragPositionRef = useRef<XYPosition | null>(null);

    // Debounced version of the core drag logic (proximity edges and overlap state)
    const debouncedDragLogic = useCallback(
        debounce((dragNode: Node) => {
            const currentPos = dragNode.position;
            const lastPos = lastDragPositionRef.current;

            if (lastPos) {
                const dx = currentPos.x - lastPos.x;
                const dy = currentPos.y - lastPos.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < 10) {
                    // Skip proximity calculation if node hasn't moved significantly
                    return;
                }
            }

            // Update last checked position
            lastDragPositionRef.current = { ...currentPos };

            // Handle proximity edges
            const closeEdge = getClosestEdge(dragNode);
            const currentEdges = useStore.getState().edges; // Get latest edges
            const nextEdges = currentEdges.filter(
                (e: Edge) =>
                    e.className !== 'temp' &&
                    e.sourceHandle !== 'null' &&
                    e.targetHandle !== 'null'
            );

            if (
                closeEdge &&
                !nextEdges.find(
                    (ne: Edge) =>
                        ne.source === closeEdge.source && ne.target === closeEdge.target,
                )
            ) {
                // Block adding invalid edges with missing handles
                if (
                    closeEdge.sourceHandle === 'null' ||
                    closeEdge.sourceHandle === null ||
                    closeEdge.sourceHandle === undefined ||
                    closeEdge.sourceHandle === '' ||
                    closeEdge.targetHandle === 'null' ||
                    closeEdge.targetHandle === null ||
                    closeEdge.targetHandle === undefined ||
                    closeEdge.targetHandle === ''
                ) {
                    // Skip adding invalid edge
                } else {
                    const tempEdge: Edge = {
                        ...closeEdge,
                        id: `temp-${closeEdge.source}-${closeEdge.target}`, // Ensure unique temp ID
                        className: 'temp',
                        type: 'custom', // Explicitly set type if needed
                    };
                    nextEdges.push(tempEdge);
                }
            }
            setEdges(nextEdges);

            // --- Overlap Logic ---
            const currentNodes = useStore.getState().nodes; // Get latest nodes
            // Find intersecting nodes (potential drop targets)
            const intersectingNodes = currentNodes.filter((n: Node) => {
                if (n.id === dragNode.id || !n.position || !n.width || !n.height || !dragNode.position) return false;

                // Simple Axis-Aligned Bounding Box (AABB) intersection test
                const dragRight = dragNode.position.x + (dragNode.width || 0);
                const dragBottom = dragNode.position.y + (dragNode.height || 0);
                const nodeRight = n.position.x + n.width;
                const nodeBottom = n.position.y + n.height;

                return (
                    dragNode.position.x < nodeRight &&
                    dragRight > n.position.x &&
                    dragNode.position.y < nodeBottom &&
                    dragBottom > n.position.y
                );
            });

            const overlappingNode = intersectingNodes[0]; // Prioritize the first found overlap
            overlappingNodeRef.current = overlappingNode || null; // Update ref for electrical logic

            // --- Group Hover Highlighting ---
            // Check if the overlapping node is a group and update store
            const intersectingGroup = intersectingNodes.find(n => n.type === 'group');
            setHoveredGroupId(intersectingGroup?.id ?? null);
            // --- End Group Hover Highlighting ---


            // Update the dragged node's state based on overlap (for visual feedback - electrical components)
            // We might remove this if GroupNode handles its own visual state based on parentId changes later
            setNodes((prevNodes: Node[]) =>
                prevNodes.map((node) => {
                    if (node.id === dragNode.id && node.type !== 'group') { // Don't apply electrical state to groups
                        let overlapState: NodeHoverState | undefined = undefined;
                        if (overlappingNode && overlappingNode.data?.type && dragNode.data?.type && overlappingNode.type !== 'group') { // Check overlap node is not group
                            const isOverlappingComponent = [
                                NodeCategory.Capacitor,
                                NodeCategory.Resistor,
                                NodeCategory.Inductor,
                            ].includes(overlappingNode.data.type as NodeCategory);

                            if (isOverlappingComponent) {
                                overlapState = overlappingNode.data.type === dragNode.data.type
                                    ? NodeHoverState.Add
                                    : NodeHoverState.NotAdd;
                            }
                        }
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                state: overlapState, // Set state based on overlap
                            },
                        };
                    }
                    return node;
                })
            );
        }, 250), // Debounce wait time: 250ms (increased from 100ms)
        [getClosestEdge, setEdges, setNodes, setHoveredGroupId] // Dependencies for the debounced function
    );


    const onNodeDrag: NodeDragHandler = useCallback(
        (_evt: React.MouseEvent, dragNode: Node) => {
            if (isEmbedded) return;
            
            // Get the current nodes from the store
            const currentNodes = useStore.getState().nodes;

            // Find the node being dragged
            const node = currentNodes.find(n => n.id === dragNode.id);
            if (!node) return;

            // Create a position change object for the helper lines function
            const change = {
                id: dragNode.id,
                type: 'position' as const,
                position: dragNode.position,
                dragging: true
            };

            // Use a simpler approach with fixed large bounds
            // This will make helper lines extend across the entire visible area
            const viewBounds = {
                x: -5000,
                y: -5000,
                x2: 5000,
                y2: 5000
            };

            // If shift is pressed, don't apply any snapping or helper lines
            if (isShiftPressed) {
                // Clear helper lines when shift is pressed
                setHelperLines(undefined, undefined, [], []);
                return; // Skip all snapping logic
            }

            // Only calculate helper lines if shift is not pressed
            const helperLines = getHelperLines(change, currentNodes, node?.parentId ?? null, viewBounds, true);

            // Apply snap position if available
            if (helperLines.snapPosition.x !== undefined || helperLines.snapPosition.y !== undefined) {
                // Update the node's position in the React Flow state
                setNodes(nodes =>
                    nodes.map(n => {
                        if (n.id === dragNode.id) {
                            return {
                                ...n,
                                position: {
                                    x: helperLines.snapPosition.x ?? n.position.x,
                                    y: helperLines.snapPosition.y ?? n.position.y
                                }
                            };
                        }
                        return n;
                    })
                );
            }

            // Extract different types of lines from the result
            const horizontalLine = helperLines.lines.find((line: HelperLineSegment) => line.y1 === line.y2 && line.style === 'align');
            const verticalLine = helperLines.lines.find((line: HelperLineSegment) => line.x1 === line.x2 && line.style === 'align');
            const fixedLines = helperLines.lines.filter((line: HelperLineSegment) => line.style === 'fixed');
            const spacingLines = helperLines.lines.filter((line: HelperLineSegment) => line.style === 'spacing');

            // Update helper lines in the store
            setHelperLines(
                horizontalLine ? horizontalLine.y1 : undefined,
                verticalLine ? verticalLine.x1 : undefined,
                fixedLines,
                spacingLines
            );
            
            // Call the debounced logic for proximity and overlap state updates
            debouncedDragLogic(dragNode);

            // --- Copy Preview Logic ---
            if (isAltPressed) {
                lastPreviewPositionRef.current = dragNode.position; // Store current top-left position

                if (!previewNodeIdRef.current) {
                    const id = `preview-${dragNode.id}-${+new Date()}`;
                    previewNodeIdRef.current = id;
                    const newPreviewNode: Node = {
                        ...dragNode,
                        id,
                        position: dragNode.position, // Position it at the dragged node's current position
                        data: { ...dragNode.data },
                        style: { ...dragNode.style, opacity: 0.7, zIndex: 1000 },
                        selected: false,
                        dragging: false, // Preview should not be draggable itself
                        type: dragNode.type, // Ensure type is preserved
                    };
                    // Ensure positionAbsolute is not carried over if it exists, React Flow will recalc
                    delete (newPreviewNode as any).positionAbsolute; 
                    setReactFlowNodes((nds) => [...nds, newPreviewNode]);
                } else {
                    // Update existing preview node's position
                    setReactFlowNodes((nds) =>
                        nds.map((n) =>
                            n.id === previewNodeIdRef.current
                                ? { ...n, position: dragNode.position }
                                : n
                        )
                    );
                }
            } else {
                // Alt is not pressed, remove preview if it exists
                if (previewNodeIdRef.current) {
                    setReactFlowNodes((nds) => nds.filter(n => n.id !== previewNodeIdRef.current));
                    previewNodeIdRef.current = null;
                    lastPreviewPositionRef.current = null;
                }
            }
        },
        [isEmbedded, isShiftPressed, debouncedDragLogic, setNodes, setHelperLines, isAltPressed, setReactFlowNodes], // Added isAltPressed, setReactFlowNodes
    );

    const onNodeDragStop: NodeDragHandler = useCallback(
        (_event: React.MouseEvent, draggedNode: Node) => { // Renamed dragNode to draggedNode for clarity
            if (isEmbedded) return;

            setIsNodeDragging(false);

            // Check if Alt is pressed on release to trigger copy
            if (isAltPressed) {
                // --- Alt+Drag COPY Logic ---
                let finalPositionTopLeft: XYPosition;
                if (lastPreviewPositionRef.current) {
                    finalPositionTopLeft = lastPreviewPositionRef.current;
                } else {
                    // Fallback: if preview wasn't active, position relative to dragged node's final spot
                    // This case implies Alt was pressed exactly at dragStop without prior drag with Alt.
                    finalPositionTopLeft = draggedNode.position; 
                }

                // const newNodeId = `node_${+new Date()}`; // This was unused
                const allCurrentNodes = getNodes(); // Get all nodes from React Flow instance

                // Original node handling will be done after adding the new one(s)

                if (draggedNode.type === 'group') {
                    // --- Group Copy Logic ---
                    const nodesToCopy: (CustomNode | Node<PlaybookNodeData>)[] = [];
                    const idMap = new Map<string, string>();

                    const getDescendants = (nodeId: string, allNodes: Node[]): Node[] => {
                        const children = allNodes.filter(n => n.parentId === nodeId);
                        let descendants = [...children];
                        children.forEach(child => {
                            if (child.type === 'group') { // Also copy children of nested groups
                                descendants = descendants.concat(getDescendants(child.id, allNodes));
                            }
                        });
                        return descendants;
                    };

                    const descendants = getDescendants(draggedNode.id, allCurrentNodes);
                    const newGroupId = `node_${+new Date()}`;
                    idMap.set(draggedNode.id, newGroupId);

                    const newGroupNode = {
                        ...draggedNode,
                        id: newGroupId,
                        position: finalPositionTopLeft,
                        selected: true,
                        dragging: false,
                        parentId: undefined,
                        extent: undefined,
                        data: { ...draggedNode.data },
                        width: getDefaultNodeWidth(draggedNode.type), // Use default width for group
                        height: NODE_DIMENSIONS.MAX_HEIGHT, // Groups typically have a larger default height
                    };
                    nodesToCopy.push(newGroupNode as CustomNode | Node<PlaybookNodeData>);

                    descendants.forEach(descNode => {
                        const newDescNodeId = `node_${+new Date()}_${Math.random().toString(36).substr(2, 9)}`;
                        idMap.set(descNode.id, newDescNodeId);
                        const newParentId = descNode.parentId ? idMap.get(descNode.parentId) : undefined;

                        nodesToCopy.push({
                            ...descNode,
                            id: newDescNodeId,
                            position: { ...descNode.position },
                            selected: false,
                            dragging: false,
                            parentId: newParentId,
                            extent: newParentId ? 'parent' : undefined,
                            data: { ...descNode.data },
                            width: getDefaultNodeWidth(descNode.type || 'default'), // Use default width for child nodes
                            height: NODE_DIMENSIONS.MIN_HEIGHT, // Default height for child nodes
                        } as CustomNode | Node<PlaybookNodeData>);
                    });
                    
                    if (addNodesFunction) {
                        addNodesFunction(nodesToCopy);
                    } else {
                        console.warn("addNodesFunction not provided for group copy.");
                        // Fallback to adding one by one if batch function is missing (less ideal)
                        nodesToCopy.forEach(n => addNodeFunction(n));
                        takeSnapshot(); // Manual snapshot if not using a dedicated addNode
                    }

                } else {
                    // --- Single Node Copy Logic ---
                    const newNodeId = `node_${+new Date()}`;
                    let newNode: CustomNode | Node<PlaybookNodeData>;

                    if (mode === 'project') {
                        newNode = {
                            ...(draggedNode as CustomNode),
                            id: newNodeId,
                            position: finalPositionTopLeft,
                            selected: true,
                            dragging: false,
                            data: { ...draggedNode.data } as AnyNodeData & { nodeType?: string },
                            parentId: draggedNode.parentId,
                            extent: draggedNode.parentId ? 'parent' : undefined,
                            width: getDefaultNodeWidth(draggedNode.type || 'default'),
                            height: NODE_DIMENSIONS.MIN_HEIGHT,
                        } as CustomNode;
                    } else { // playbook mode
                        newNode = {
                            ...(draggedNode as Node<PlaybookNodeData>),
                            id: newNodeId,
                            position: finalPositionTopLeft,
                            selected: true,
                            dragging: false,
                            data: { ...draggedNode.data } as PlaybookNodeData,
                            parentId: draggedNode.parentId,
                            extent: draggedNode.parentId ? 'parent' : undefined,
                            width: getDefaultNodeWidth(draggedNode.type || 'default'),
                            height: NODE_DIMENSIONS.MIN_HEIGHT,
                        } as Node<PlaybookNodeData>;
                    }
                    
                    if (addNodeFunction) {
                        addNodeFunction(newNode);
                    } else {
                        if (mode === 'project') {
                            setNodes(nds => [...nds, newNode as CustomNode]);
                        } else {
                            console.warn("addNodeFunction not provided for playbook mode single copy.");
                        }
                        takeSnapshot();
                    }
                }

                // After adding new node(s), reset the original dragged node to its initial position
                if (draggedNodeInitialStateRef.current && draggedNodeInitialStateRef.current.id === draggedNode.id) {
                    const originalNodeInitialPos = draggedNodeInitialStateRef.current.position;
                    // Use Zustand's setNodes which internally calls processNodes
                    setNodes(nds => nds.map(n => {
                        if (n.id === draggedNode.id) {
                            // Ensure the node object being updated is a fresh clone for Reactivity
                            return { ...n, position: originalNodeInitialPos, selected: false, dragging: false };
                        }
                        return n;
                    }));
                } else {
                    // Fallback: if ref is not set (should not happen), just deselect
                    setNodes(nds => nds.map(n => n.id === draggedNode.id ? { ...n, selected: false, dragging: false } : n));
                }
                draggedNodeInitialStateRef.current = null;

                setHelperLines(undefined, undefined, [], []);
                return; 
            }

            // --- Original Drag Stop Logic (Move) ---
            // Clear the initial state ref if it was a move, not a copy
            draggedNodeInitialStateRef.current = null;
            const currentNodes = getNodes();
            const selectedNodes = currentNodes.filter(n => n.selected && n.id !== draggedNode.id);
            const allDraggedNodes = [draggedNode, ...selectedNodes];

            // --- Dynamic Grouping Logic ---
            const groupNodes = currentNodes.filter(n => n.type === 'group');
            let nodesNeedUpdate = false;
            let targetGroup: Node | null = null;
            let smallestGroupArea: number | null = null;

            // --- Determine Target Group based on Primary Dragged Node ---
            // Calculate center of the primary dragged node
            const primaryNodeRect = {
                x: draggedNode.positionAbsolute?.x ?? draggedNode.position.x,
                y: draggedNode.positionAbsolute?.y ?? draggedNode.position.y,
                width: draggedNode.width!,
                height: draggedNode.height!,
            };
            const primaryNodeCenter: XYPosition = {
                x: primaryNodeRect.x + primaryNodeRect.width / 2,
                y: primaryNodeRect.y + primaryNodeRect.height / 2,
            };

            // Find the group the primary node's center is inside
            for (const groupNode of groupNodes) {
                if (!groupNode.positionAbsolute || !groupNode.width || !groupNode.height) continue;
                const groupRect: Rect = {
                    x: groupNode.positionAbsolute.x,
                    y: groupNode.positionAbsolute.y,
                    width: groupNode.width,
                    height: groupNode.height,
                };

                if (
                    primaryNodeCenter.x > groupRect.x &&
                    primaryNodeCenter.x < groupRect.x + groupRect.width &&
                    primaryNodeCenter.y > groupRect.y &&
                    primaryNodeCenter.y < groupRect.y + groupRect.height
                ) {
                    const groupArea = groupRect.width * groupRect.height;
                    // If it's the first containing group found, or smaller than the current smallest
                    if (smallestGroupArea === null || groupArea < smallestGroupArea) {
                        smallestGroupArea = groupArea;
                        targetGroup = groupNode;
                    }
                    // Continue checking other groups in case there's an even smaller nested one
                }
            }
            const targetGroupId = targetGroup?.id;
            // --- End Determine Target Group ---


            // --- Apply Grouping Change to ALL Dragged Nodes ---
            const updatedNodes = currentNodes.map(node => {
                // Check if this node is one of the nodes being dragged
                const isDragged = allDraggedNodes.find(dn => dn.id === node.id);
                if (!isDragged || node.type === 'group') {
                    return node;
                }

                // Use the node's absolute position for calculations
                const nodeRect = {
                    x: node.positionAbsolute?.x ?? node.position.x,

                    y: node.positionAbsolute?.y ?? node.position.y,
                    width: node.width!,
                    height: node.height!,
                };

                const currentParentId = node.parentId;
                // Use the targetGroupId determined earlier based on the primary node

                // Case 1: Dragged INTO the target group (and not already in it)
                if (targetGroupId && currentParentId !== targetGroupId) {
                    // targetGroup is already determined and available here
                    if (!targetGroup || !targetGroup.positionAbsolute) return node;

                    const relativePosition: XYPosition = {
                        x: nodeRect.x - targetGroup.positionAbsolute.x,
                        y: nodeRect.y - targetGroup.positionAbsolute.y,
                    };
                    nodesNeedUpdate = true;
                    return {
                        ...node,
                        parentId: targetGroupId,
                        position: relativePosition,
                        positionAbsolute: undefined,
                        extent: 'parent' as const,
                        zIndex: 0,
                    };
                }
                // Case 2: Dragged OUT OF a group (targetGroup is null, but node had a parent)
                else if (!targetGroupId && currentParentId) {
                     // Check if the node's current parent is actually a group node
                     const currentParentNode = getNode(currentParentId);
                     if (currentParentNode?.type === 'group') {
                        nodesNeedUpdate = true;
                        return {
                            ...node,
                            parentId: undefined,
                            position: { x: nodeRect.x, y: nodeRect.y },
                            positionAbsolute: { x: nodeRect.x, y: nodeRect.y },
                            extent: undefined,
                            zIndex: 1,
                        };
                     }
                }
                // Case 3: No change in group status
                return node;
            });

            // Apply node updates if grouping changed
            if (nodesNeedUpdate) {
                setNodes(updatedNodes);
            }
            // --- End Dynamic Grouping Logic ---


            // --- Proximity Connection Logic ---
            // Use the primary dragged node for proximity checks
            if (!wasClickRef.current) {
                const closeEdge = getClosestEdge(draggedNode);
                const currentEdges = useStore.getState().edges;
                let nextEdges = currentEdges.filter(
                    (e: Edge) =>
                        e.className !== 'temp' &&
                        e.sourceHandle !== 'null' &&
                        e.targetHandle !== 'null'
                );

                if (closeEdge) {
                    const currentNodes = useStore.getState().nodes;
                    const sourceNode = currentNodes.find(n => n.id === closeEdge.source);
                    const targetNode = currentNodes.find(n => n.id === closeEdge.target);

                    const isDestinationToDestination =
                        sourceNode?.type === 'destination' && targetNode?.type === 'destination';

                    const edgeAlreadyExists = nextEdges.some(
                        (ne: Edge) => ne.source === closeEdge.source && ne.target === closeEdge.target
                    );

                    if (!edgeAlreadyExists && !isDestinationToDestination) {
                        // Use null handles for non-group proximity nodes
                        const sourceHandle = sourceNode?.type === 'group' ? `${sourceNode.id}-source` : null;
                        const targetHandle = targetNode?.type === 'group' ? `${targetNode.id}-target` : null;

                        // Block adding invalid edges with missing handles
                        if (
                            sourceHandle === 'null' ||
                            sourceHandle === null ||
                            sourceHandle === undefined ||
                            sourceHandle === '' ||
                            targetHandle === 'null' ||
                            targetHandle === null ||
                            targetHandle === undefined ||
                            targetHandle === ''
                        ) {
                            // Skip adding invalid edge
                            setEdges(nextEdges);
                        } else {
                            const finalEdge: Edge = {
                                id: `edge-${closeEdge.source}-${closeEdge.target}`,
                                source: closeEdge.source,
                                target: closeEdge.target,
                                type: 'custom',
                                sourceHandle: sourceHandle,
                                targetHandle: targetHandle,
                                // Add other default edge properties if needed
                            };
                            nextEdges.push(finalEdge);
                            setEdges(nextEdges);
                        }
                    } else if (isDestinationToDestination) {
                        showNotification('error', 'Cannot connect destination nodes to each other', 3000);
                        // Remove the temp edge if it exists but wasn't converted
                        setEdges(nextEdges);
                    } else {
                         // Edge already exists or other issue, just remove temp edge
                         setEdges(nextEdges);
                    }
                } else {
                    // No close edge found, ensure temp edges are removed
                    setEdges(nextEdges);
                }
            } else {
                 // It was a click, ensure temp edges are removed
                 const currentEdges = useStore.getState().edges;
                 setEdges(currentEdges.filter(
                    (e: Edge) =>
                        e.className !== 'temp' &&
                        e.sourceHandle !== 'null' &&
                        e.targetHandle !== 'null' &&
                        e.sourceHandle != 'null' &&
                        e.targetHandle != 'null'
                ));
            }

            // --- Overlapping Node Logic (Board/Component Interaction) ---
            // This logic might need adjustment if groups can contain electrical components
            // For now, assume groups don't directly interact with the electrical component logic
            const overlappingNode = overlappingNodeRef.current;

            // Check if the primary dragged node is an electrical component type before proceeding
            const isSpecialDragged = draggedNode.data?.type &&
                [NodeCategory.Board, NodeCategory.Capacitor, NodeCategory.Resistor, NodeCategory.Inductor]
                    .includes(draggedNode.data?.type as NodeCategory);

            if (isSpecialDragged && !nodesNeedUpdate) {
                // 1. Detach from Parent Board if not dropped on a board or dropped outside original parent
                if (draggedNode.parentId && (!overlappingNode || overlappingNode.type !== NodeCategory.Board || overlappingNode.id !== draggedNode.parentId)) {
                    setNodes((prevNodes: Node[]) => {
                        const board = prevNodes.find((prevNode) => prevNode.id === draggedNode.parentId);
                        // Ensure the parent was actually a board before detaching
                        if (board?.type !== NodeCategory.Board) return prevNodes;

                        return prevNodes.map((node) => {
                            if (node.id === draggedNode.id) {
                                // Position should already be absolute after drag, no need to recalculate from relative
                                return { ...node, parentId: undefined, extent: undefined };
                            }
                            return node;
                        });
                    });
                }

                // 2. Combine Component Values if dropped on same type
                if (overlappingNode && draggedNode.data?.type &&
                    [NodeCategory.Capacitor, NodeCategory.Resistor, NodeCategory.Inductor]
                        .includes(overlappingNode.data?.type as NodeCategory) &&
                    draggedNode.data.type === overlappingNode.data.type)
                {
                    setNodes((prevNodes: Node[]) =>
                        prevNodes
                            .map((node) => {
                                if (node.id === overlappingNode.id) {
                                    const dragValue = (draggedNode.data?.value as number) || 0;
                                    const overlapValue = (node.data?.value as number) || 0;
                                    return {
                                        ...node,
                                        data: {
                                            ...node.data,
                                            value: overlapValue + dragValue,
                                        },
                                    };
                                }
                                return node;
                            })
                            .filter((node) => node.id !== draggedNode.id)
                    );
                }
                // 3. Attach Node to Board if dropped on a board
                else if (overlappingNode && overlappingNode.type === NodeCategory.Board) {
                    setNodes((prevNodes: Node[]) => {
                        const boardPosition = overlappingNode.positionAbsolute || overlappingNode.position || { x: 0, y: 0 };
                        const dragPosition = draggedNode.positionAbsolute || draggedNode.position || { x: 0, y: 0 };

                        // Calculate relative position based on absolute positions
                        const relativePosition = { x: dragPosition.x - boardPosition.x, y: dragPosition.y - boardPosition.y };

                        // Update the node being dropped
                        const updatedAttachNodes = prevNodes.map((node) => {
                            if (node.id === draggedNode.id) {
                                return {
                                    ...node,
                                    parentId: overlappingNode.id,
                                    position: relativePosition,
                                    positionAbsolute: undefined,
                                    extent: 'parent',
                                    draggable: true,
                                    data: { ...node.data },
                                } as CustomNode;
                            }
                            return node;
                        });

                        // Ensure the board (parent node) is rendered before its children
                        const boardIndex = updatedAttachNodes.findIndex(n => n.id === overlappingNode.id);
                        const droppedNodeIndex = updatedAttachNodes.findIndex(n => n.id === draggedNode.id);

                        if (boardIndex > -1 && droppedNodeIndex > -1 && boardIndex > droppedNodeIndex) {
                            const boardNode = updatedAttachNodes.splice(boardIndex, 1)[0];
                            updatedAttachNodes.splice(droppedNodeIndex, 0, boardNode);
                        }

                        return updatedAttachNodes;
                    });
                }
            }


            // --- Final Cleanup ---
            // Recalculate latencies (now debounced in the store, triggered by state updates)
            // The direct call here is removed.

            // Reset refs
            wasClickRef.current = false;
            overlappingNodeRef.current = null;
            setHoveredGroupId(null);
            
            // Always clear helper lines when drag stops, regardless of shift key state
            setHelperLines(undefined, undefined, [], []);
            

            // --- Final Cleanup for Preview ---
            if (previewNodeIdRef.current) {
                setReactFlowNodes((nds) => nds.filter(n => n.id !== previewNodeIdRef.current));
                previewNodeIdRef.current = null;
            }
            lastPreviewPositionRef.current = null;
        },
        [isEmbedded, getClosestEdge, setNodes, setEdges, showNotification, takeSnapshot, setIsNodeDragging, getNodes, getNode, setHoveredGroupId, setHelperLines, isAltPressed, project, addNodeFunction, addNodesFunction, mode, setReactFlowNodes],
    );

    return {
        onNodeDragStart,
        onNodeDrag,
        onNodeDragStop,
    };
};
